import { Component, computed, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TopologicalDeviceTelemetryStore } from '@root/app/store/topological-device-telemetry.store';
import { ChartOptions } from 'chart.js';
import {
  ChartComponent,
  XemexChartDataset,
} from '@custom-components/general/chart/chart.component';
import {
  ChartBuilder,
  ChartType,
  toChartDatasets,
  toChartLabels,
  toChartNote,
  toChartBadge,
} from '../chartBuilder';

@Component({
  selector: 'xemex-heatpump-insights',
  imports: [ChartComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './heatpump.component.html',
})
export class HeatpumpComponent {
  telemetryStore = inject(TopologicalDeviceTelemetryStore);
  translateService = inject(TranslateService);
  channelNumber = input.required<number>();
  colors = {
    flowRate: '#2121A1',
    boilerTarget: '#2596be',
    domesticWater: '#A6AB3C',
    leavingWater: '#AB55BA',
    outsideAir: '#4F8B52',
    returnWater: '#7C655D',
    thermostatTarget: '#F4820E',
    room: '#D9342B',
  };

  public chartOptions: ChartOptions = {
    maintainAspectRatio: false,
    interaction: { intersect: false, axis: 'xy' },
    scales: {
      x: { grid: { display: false } },
      y: {
        min: -30,
        max: 80,
        ticks: {
          callback: (item: any) => `${item} °C`,
        },
      },
      flow: {
        min: 0,
        ticks: {
          callback: (item: any) => `${item} l/m`,
        },
        grid: { display: false },
        position: 'right',
      },
    },
    plugins: {
      legend: { position: 'bottom' },
    },
  };

  private readonly _chartData = computed(() => {
    if (this.telemetryStore.isLoadingDeviceTelemetry()) return undefined;

    const chartBuilder = new ChartBuilder(this.telemetryStore, this.translateService);
    chartBuilder
      .withChartSetup()
      .withChannelData(
        ChartType.LINE,
        (d, c) => d.heatPump.find(x => x.channelNumber == c)?.boilerTargetTemperature ?? null,
        i => i.heatPump.filter(x => x.channelNumber === this.channelNumber()),
        'devices.detailTabs.insights.heatpump.boiler_target_temperature',
        this.colors.boilerTarget,
      )
      .withChannelData(
        ChartType.LINE,
        (d, c) => d.heatPump.find(x => x.channelNumber == c)?.domesticHotWaterTemperature ?? null,
        i => i.heatPump.filter(x => x.channelNumber === this.channelNumber()),
        'devices.detailTabs.insights.heatpump.domestic_hot_water_temperature',
        this.colors.domesticWater,
      )
      .withChannelData(
        ChartType.LINE,
        (d, c) => {
          const hp = d.heatPump.find(x => x.channelNumber == c);
          return hp?.flowRate != null ? hp.flowRate * 1000 * 60 : null;
        },
        i => i.heatPump.filter(x => x.channelNumber === this.channelNumber()),
        'devices.detailTabs.insights.heatpump.flow_rate',
        this.colors.flowRate,
        'flow',
      )
      .withChannelData(
        ChartType.LINE,
        (d, c) =>
          d.heatPump.find(x => x.channelNumber == c)?.leavingWaterTempPreBackupHeater ?? null,
        i => i.heatPump.filter(x => x.channelNumber === this.channelNumber()),
        'devices.detailTabs.insights.heatpump.leaving_water_temp_pre_backup_heater',
        this.colors.leavingWater,
      )
      .withChannelData(
        ChartType.LINE,
        (d, c) => d.heatPump.find(x => x.channelNumber == c)?.roomTemperature ?? null,
        i => i.heatPump.filter(x => x.channelNumber === this.channelNumber()),
        'devices.detailTabs.insights.heatpump.room_temperature',
        this.colors.room,
      )
      .withChannelData(
        ChartType.LINE,
        (d, c) => d.heatPump.find(x => x.channelNumber == c)?.outsideAirTemperature ?? null,
        i => i.heatPump.filter(x => x.channelNumber === this.channelNumber()),
        'devices.detailTabs.insights.heatpump.outside_air_temperature',
        this.colors.outsideAir,
      )
      .withChannelData(
        ChartType.LINE,
        (d, c) => d.heatPump.find(x => x.channelNumber == c)?.returnWaterTemperature ?? null,
        i => i.heatPump.filter(x => x.channelNumber === this.channelNumber()),
        'devices.detailTabs.insights.heatpump.return_water_temperature',
        this.colors.returnWater,
      )
      .withChannelData(
        ChartType.LINE,
        (d, c) => d.heatPump.find(x => x.channelNumber == c)?.thermostatTargetTemperature ?? null,
        i => i.heatPump.filter(x => x.channelNumber === this.channelNumber()),
        'devices.detailTabs.insights.heatpump.thermostat_target_temperature',
        this.colors.thermostatTarget,
      );

    return chartBuilder.build();
  });

  chartLabels = computed<string[]>(() => toChartLabels(this._chartData()));
  chartNote = computed(() => toChartNote(this._chartData()));
  chartBadge = computed(() => toChartBadge(this._chartData()));

  chartDatasets = computed<XemexChartDataset[]>(() => toChartDatasets(this._chartData()));
}
